<template>
  <div class="wallets-list">
    <!-- Loading -->
    <div v-if="loading" class="loading">{{ $t('bo.common.loading') }}</div>

    <!-- Error -->
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <!-- Empty -->
    <div v-else-if="wallets.length === 0" class="empty">{{ $t('bo.billing.noWallets') }}</div>

    <!-- Table -->
    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.billing.colCompany') }}</th>
          <th>{{ $t('bo.billing.colBalance') }}</th>
          <th>{{ $t('bo.billing.colThreshold') }}</th>
          <th>{{ $t('bo.billing.colStatus') }}</th>
          <th>{{ $t('bo.billing.colUpdated') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="wallet in wallets" :key="wallet.id">
          <td class="col-company">{{ wallet.company?.name || '—' }}</td>
          <td class="col-balance">
            <span :class="['balance', { 'balance-low': isLow(wallet) }]">
              {{ wallet.balance }} {{ wallet.currency }}
            </span>
          </td>
          <td>{{ wallet.low_balance_threshold ?? 5 }}</td>
          <td>
            <span v-if="isLow(wallet)" class="badge badge-warning">{{ $t('bo.billing.lowBalance') }}</span>
            <span v-else class="badge badge-ok">{{ $t('bo.billing.ok') }}</span>
          </td>
          <td class="col-date">{{ formatDate(wallet.updated_at) }}</td>
          <td class="actions">
            <button @click="openDetail(wallet.company_id)" class="btn-action btn-view">
              {{ $t('bo.billing.viewDetail') }}
            </button>
            <button @click="openAdjust(wallet.company_id)" class="btn-action btn-credit">
              {{ $t('bo.billing.addCredits') }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="pagination && pagination.last_page > 1" class="pagination">
      <button :disabled="pagination.current_page === 1" @click="changePage(pagination.current_page - 1)" class="page-btn">&larr;</button>
      <span class="page-info">{{ pagination.current_page }} / {{ pagination.last_page }}</span>
      <button :disabled="pagination.current_page === pagination.last_page" @click="changePage(pagination.current_page + 1)" class="page-btn">&rarr;</button>
    </div>

    <WalletDetailModal
      v-if="selectedCompanyId"
      :company-id="selectedCompanyId"
      @close="selectedCompanyId = null"
      @adjusted="reload"
    />

    <AdjustModal
      v-if="adjustCompanyId"
      :company-id="adjustCompanyId"
      @close="adjustCompanyId = null"
      @adjusted="() => { adjustCompanyId = null; reload() }"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import billingService from '@/services/billingService'
import WalletDetailModal from './WalletDetailModal.vue'
import AdjustModal from './AdjustModal.vue'

const { t } = useI18n()

const wallets = ref([])
const loading = ref(false)
const error = ref('')
const pagination = ref(null)
const currentPage = ref(1)
const selectedCompanyId = ref(null)
const adjustCompanyId = ref(null)

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await billingService.getWallets({ page: currentPage.value })
    const data = res.data?.data
    wallets.value = data?.data || []
    pagination.value = data ? { current_page: data.current_page, last_page: data.last_page, total: data.total } : null
  } catch {
    error.value = t('bo.billing.failedLoad')
  } finally {
    loading.value = false
  }
}

const reload = () => load()
const changePage = (page) => { currentPage.value = page; load() }
const openDetail = (companyId) => { selectedCompanyId.value = companyId }
const openAdjust = (companyId) => { adjustCompanyId.value = companyId }
const isLow = (wallet) => wallet.balance <= (wallet.low_balance_threshold ?? 5)
const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—'

onMounted(load)
</script>

<style scoped>
.wallets-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.table tr:last-child td { border-bottom: none; }

.col-company { font-weight: 600; color: #1f2937; }
.col-date { color: #6b7280; font-size: 0.8rem; }

.balance { font-weight: 700; color: #1f2937; }
.balance-low { color: #dc2626; }

.badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.badge-ok { background: #d1fae5; color: #065f46; }
.badge-warning { background: #fee2e2; color: #991b1b; }

.actions { display: flex; gap: 0.4rem; }

.btn-action {
  padding: 0.3rem 0.6rem;
  border-radius: 0.375rem;
  font-size: 0.775rem;
  cursor: pointer;
  border: 1px solid;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-view { border-color: #3b82f6; color: #3b82f6; background: white; }
.btn-view:hover { background: #eff6ff; }

.btn-credit { border-color: #10b981; color: #10b981; background: white; }
.btn-credit:hover { background: #ecfdf5; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f9fafb; }

.page-info { font-size: 0.875rem; color: #6b7280; }
</style>
